import { api, qs } from './client.js';
import type { Paginated } from './types.js';

export type FilterType =
  | 'search'
  | 'equals'
  | 'select'
  | 'dateFrom'
  | 'dateTo'
  | 'numFrom'
  | 'numTo'
  | 'flag';

export type ColumnType =
  | 'text'
  | 'number'
  | 'money'
  | 'date'
  | 'datetime'
  | 'image'
  | 'link'
  | 'badge'
  | 'grade'
  | 'enum'
  | 'rowNumber';

export interface ReportFilterMeta {
  key: string;
  label: string;
  type: FilterType;
  /**
   * parent 는 dependsOn 이 걸린 필터에만 있다. 같은 value 가 상위 값 여러 개에
   * 걸쳐 있으면 그만큼 여러 번 들어온다 - 화면이 value 로 접어 그린다.
   */
  options?: { value: string; label: string; parent?: string }[];
  defaultValue?: string;
  placeholder?: string;
  /** true 면 필터 UI 칸을 안 그린다 - 화면 전용 버튼이 URL 파라미터로만 거는 필터. */
  hidden?: boolean;
  /** 이 선택지가 다른 필터(그 키)의 선택값에 따라 좁혀진다 (예: 메인 이벤트 -> 서브 이벤트). */
  dependsOn?: string;
}

export interface ReportColumnMeta {
  key: string;
  label: string;
  type?: ColumnType;
  align?: 'left' | 'right' | 'center';
  /** th/td 에 그대로 넣는 CSS width 값 (예: '12%', '140px'). */
  width?: string;
  /** 넘치는 글자를 줄바꿈 대신 '…' 으로 자른다 (긴 상품명이 행을 늘리는 걸 막을 때). */
  clip?: boolean;
  /** date 전용: 값이 없을 때 조용한 '-' 대신 빨간 글자로 보여줄 말 (예: '예약없음'). */
  emptyLabel?: string;
  /** datetime 전용: 분까지가 아니라 초까지 보여준다 (같은 분에 여러 건이 겹칠 때). */
  withSeconds?: boolean;
  /** 표 왼쪽에 붙박아 가로 스크롤해도 계속 보인다 (엑셀의 틀 고정). width 가 px 여야 한다. */
  pinned?: boolean;
  /** true 면 표에서 바로 textarea + 저장 버튼으로 고칠 수 있다 (meta.memoPk 참고). */
  editable?: boolean;
  /** rowNumber 전용: 'total'(기본, 내려 셈) 또는 'offset'(올려 셈). */
  rowNumberFrom?: 'total' | 'offset';
  sortable?: boolean;
  hrefTemplate?: string;
  srcTemplate?: string;
  /** image 전용: 마우스오버 때 띄울 이미지 주소 목록이 담긴 행 키. */
  previewImagesKey?: string;
  /** image 전용: 썸네일 폭(px). 생략하면 기본 크기(30px)다. */
  imageWidth?: number;
  badgeColors?: Record<string, string>;
  valueLabels?: Record<string, string>;
  percentOf?: string;
  /** 값을 강조색(accent)·굵게 그린다 (원본이 파랗게 칠하던 핵심 숫자). */
  emphasis?: boolean;
  /** 'stock' - 초록 머리칸 + 진한 초록 굵은 값 (원본 종합 품절체크의 재고량). */
  highlight?: 'stock';
  extract?: { from: string; pattern: string };
  hidden?: boolean;
}

export interface ReportRowWarn {
  column: string;
  lessThanColumn: string;
  exceptColumns?: string[];
}

export interface ReportSectionBy {
  column: string;
  labelColumns: { label: string; column: string }[];
}

/**
 * 한 컬럼 값으로 표를 둘로 쪼갠다 (sales-analysis 의 진행중 / 행사 종료).
 * sectionBy 와 달리 제목·합계가 표마다 따로 난다.
 */
export interface ReportSplitBy {
  column: string;
  value: string | number;
  /** {count} 는 그 표의 건수로 바뀐다. */
  primaryTitle: string;
  splitTitle: string;
  tone?: 'danger';
  /** 표마다 화면에서 더해 합계 행을 낼 컬럼들. */
  totalColumns?: string[];
}

/**
 * 같은 값이 이어지는 행들의 몇몇 칸을 세로로 합쳐 그린다 (rowspan).
 * sales-analysis 의 상품코드·이미지·상품명 - 옵션 행이 여러 줄이어도
 * 상품 정보는 맨 윗줄에 한 번만 나온다.
 */
export interface ReportMergeRows {
  by: string;
  columns: string[];
}

export interface ReportMeta {
  id: string;
  title: string;
  group: string;
  legacyPath: string;
  description?: string;
  filters: ReportFilterMeta[];
  columns: ReportColumnMeta[];
  defaultSort?: string;
  defaultDirection: 'ASC' | 'DESC';
  defaultPerPage: number;
  totals?: string[];
  restricted: boolean;
  requireFilter?: boolean;
  rowWarn?: ReportRowWarn;
  /** 이 컬럼이 비어 있는 행을 경고색으로 칠한다 (원본의 분홍 배경). */
  rowEmptyWarn?: string;
  sectionBy?: ReportSectionBy;
  splitBy?: ReportSplitBy;
  mergeRows?: ReportMergeRows;
  /** 컬럼이 많아 화면 폭을 전부 쓰고 촘촘하게 그려야 하는 표. */
  fullWidth?: boolean;
  /** editable 컬럼이 있으면, 행에서 이 키의 값으로 PATCH /memo 를 부른다. */
  memoPk?: string;
}

export interface ReportListItem {
  id: string;
  title: string;
  group: string;
  legacyPath: string;
  description?: string;
  restricted: boolean;
}

export type ReportRow = Record<string, unknown>;

export interface ReportData extends Paginated<ReportRow> {
  columns: ReportColumnMeta[];
  title: string;
  legacyPath: string;
  totals?: Record<string, number>;
}

/**
 * 체험단 신청 정보 (banana_custom.bnn_sextoy_exp_new).
 * 원본 event/bnn_sextoy_exp_edit_new.php 의 수정 폼 입력값 그대로다 - 컬럼명을
 * 바꾸지 않고 그대로 쓴다(서버도 같은 이름으로 받는다).
 */
export interface ExpApplicantInput {
  uname: string;
  hphone: string;
  email: string;
  incomename: string;
  postold: string;
  postnew: string;
  address: string;
  status: string;
  memo: string;
  etcinfo1: string;
}

/**
 * 파트너 랜딩 조건 (banana_custom.adm_partner_list).
 * 원본 partner_manage_update.php 의 폼 입력값 그대로다 - 컬럼명을 바꾸지 않고
 * 그대로 쓴다(서버도 같은 이름으로 받는다).
 *
 * 불리언 칸은 'Y'/'N' 이 아니라 **'t'/'f'** 다 (원본 partner_manage_config.php).
 */
export interface PartnerLandingInput {
  if_name: string;
  partner_id: string;
  if_middle: string;
  if_value: string;
  isuse: string;
  _utm_source: string;
  _utm_medium: string;
  _utm_campaign: string;
  _utm_content: string;
  if_bnid: string;
  if_bnid_url: string;
  doneurl: string;
  if_404: string;
  if_auth: string;
  if_adcode: string;
  if_addurl: string;
  byalign: number;
}

export const reportsApi = {
  list: () => api.get<ReportListItem[]>('/reports'),

  meta: (id: string) => api.get<ReportMeta>(`/reports/${id}/meta`),

  data: (id: string, params: Record<string, string | number | undefined>) =>
    api.get<ReportData>(`/reports/${id}${qs(params)}`),

  /** CSV 는 서버가 attachment 로 내려주므로 URL 만 만들어 준다. */
  exportUrl: (id: string, params: Record<string, string | number | undefined>) =>
    `/api/reports/${id}/export${qs(params)}`,

  saveMemo: (id: string, pk: string | number, memo: string) =>
    api.patch<{ ok: true }>(`/reports/${id}/memo`, { pk, memo }),

  /** 재고 확인(zaiko-stock) 상단의 즐겨찾기 버튼 4개. 원본 zaiko_kin_button.php. */
  zaikoKinButtons: () => api.get<ZaikoKinButton[]>('/reports/zaiko-stock/kin-buttons'),

  updateZaikoKinButtons: (buttons: { seq: number; name: string }[]) =>
    api.patch<{ ok: true }>('/reports/zaiko-stock/kin-buttons', { buttons }),

  /** 행마다 있는 즐겨찾기 1~4번 버튼. 원본 zaiko_kin_add.php. */
  addZaikoFavorite: (body: { kubun: number; goodsIdx: string; barcode: string }) =>
    api.post<{ ok: true; duplicate: boolean }>('/reports/zaiko-stock/favorites', body),

  /**
   * 즐겨찾기 해제(원본 zaiko_kin_del.php). 공용 api.del() 이 본문을 안 받아서
   * 쿼리스트링으로 보낸다 - 서버도 같은 자리(req.query)에서 읽는다.
   */
  removeZaikoFavorite: (body: { kubun: number; goodsIdx: string; barcode: string }) =>
    api.del<{ ok: true; removed: boolean }>(`/reports/zaiko-stock/favorites${qs(body)}`),

  /**
   * 파트너 랜딩수정(partner-landing)의 조건 추가·수정. 원본
   * partner_manage_update.php 의 addexe·modifyexe 자리다 (PartnerLandingPage.tsx
   * 의 모달 - 새 조건이면 seq 없이 이 addexe 를, 있으면 modifyexe 를 부른다).
   */
  createPartnerLanding: (body: PartnerLandingInput) =>
    api.post<{ ok: true; seq: number }>('/reports/partner-landing', body),

  updatePartnerLanding: (seq: number, body: PartnerLandingInput) =>
    api.patch<{ ok: true; seq: number }>(`/reports/partner-landing/${seq}`, body),

  /**
   * 체험단 현황(exp-applicants, exp-applicants-option)의 신청 정보 수정. 원본
   * event/bnn_sextoy_exp_edit_new.php 의 mode=exec 자리다 (ReportPage.tsx 의
   * ExpApplicantsEditModal). 두 리포트가 컬럼이 같아 reportId 만 바꿔 같은
   * 엔드포인트 모양을 재사용한다.
   */
  updateExpApplicant: (reportId: 'exp-applicants' | 'exp-applicants-option', seq: number, body: ExpApplicantInput) =>
    api.patch<{ ok: true; seq: number }>(`/reports/${reportId}/${seq}`, body),

  /** 체험단 현황에서 고른 신청자들의 상태를 한꺼번에 바꾼다 (원본 mode=changestate). */
  batchExpApplicantStatus: (seqs: number[], status: string) =>
    api.post<{ ok: true; affected: number }>('/reports/exp-applicants/status', { seqs, status }),

  /** 고른 신청자들의 비즈호스트 주문서 엑셀(.xlsx) (원본 mode=downcsv4). */
  expApplicantOrderExcel: (seqs: number[], barcode: string) =>
    api.postBlob('/reports/exp-applicants/order-excel', { seqs, barcode }),

  /** 고른 신청자들의 CNP폼 엑셀(.xlsx, 머리줄 없음) (원본 mode=downcsv6). */
  expApplicantCnpExcel: (seqs: number[]) =>
    api.postBlob('/reports/exp-applicants/cnp-excel', { seqs }),

  /**
   * 이벤트 판매추이 분석(sales-analysis)의 행 추가·수정·삭제.
   * 원본 prize_copy.php · prize_modify.php 를 대신한다 (SalesAnalysisPage.tsx).
   */
  createSalesAnalysisRow: (body: SalesAnalysisRowInput) =>
    api.post<{ ok: true; seq: number | null }>('/reports/sales-analysis/rows', body),

  updateSalesAnalysisRow: (seq: number, body: SalesAnalysisRowInput) =>
    api.put<{ ok: true }>(`/reports/sales-analysis/rows/${seq}`, body),

  deleteSalesAnalysisRow: (seq: number) =>
    api.del<{ ok: true }>(`/reports/sales-analysis/rows/${seq}`),

  /** '+ 상품 추가' 의 상품 조회 (원본 sales_analysis_add_ajax.php). */
  salesAnalysisGoods: (goodsIdx: string) =>
    api.get<SalesAnalysisGoods>(`/reports/sales-analysis/goods/${encodeURIComponent(goodsIdx)}`),

  /** 체크한 옵션마다 행을 하나씩 등록한다 (원본 sales_analysis_add.php 의 modifyexe). */
  createSalesAnalysisRows: (rows: SalesAnalysisRowInput[]) =>
    api.post<{ ok: true; inserted: number; skipped: number }>('/reports/sales-analysis/rows/bulk', {
      rows,
    }),

  /** 이벤트 관리(event-list)의 등록·복사·수정. */
  createEventListRow: (body: EventListRowInput) =>
    api.post<{ ok: true; seq: number | null }>('/reports/event-list/rows', body),

  updateEventListRow: (seq: number, body: EventListRowInput) =>
    api.put<{ ok: true }>(`/reports/event-list/rows/${seq}`, body),
};

export interface EventListRowInput {
  eventname: string;
  stats: '진행' | '종료' | '대기';
  start_date: string;
  end_date: string;
  link: string;
  up_name: string;
  memo: string;
}

/** 상품코드 하나로 찾은 상품과 그 옵션(바코드) 줄들. */
export interface SalesAnalysisGoods {
  goodsIdx: string;
  gname: string;
  /** 상품의 gname_pre2 - 원본이 판매시작일로 그대로 쓰던 값 */
  openDate: string;
  /** 상품명 말머리로 정해지는 이벤트 종류 (1 나홀로 오나홀 · 2 생애 첫 바이브) */
  eventType: number;
  options: SalesAnalysisGoodsOption[];
}

export interface SalesAnalysisGoodsOption {
  barcode: string;
  opCode: string;
  opName: string;
  thumbnail: string;
  daccount: number;
  account: number;
  /** 이 화면에 이미 등록해 둔 조합인가 */
  registered: boolean;
}

/**
 * 판매추이 분석 한 행의 고칠 수 있는 값들. 키는 DB 컬럼명 그대로다 -
 * 서버 쪽 zod 스키마(reports/index.ts 의 salesAnalysisBody)와 짝이다.
 */
export interface SalesAnalysisRowInput {
  /** 1 나홀로 오나홀 · 2 생애 첫 바이브 · 0 둘 다 아님 */
  event_type: number;
  goods_idx: string;
  barcode: string;
  thumbnail: string;
  gname: string;
  op_code: string;
  op_name: string;
  daccount: number;
  account: number;
  quantity: number;
  memo: string;
  /** 'YYYY-MM-DD' 또는 '' (비우면 서버가 NULL 로 저장한다) */
  open_date: string;
  end_flag: 0 | 1;
  end_date: string;
}

export interface ZaikoKinButton {
  seq: number;
  kubun: number;
  name: string;
}
